import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Avatar,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Grid,
  Divider,
  Snackbar,
  InputAdornment,
  CircularProgress
} from '@material-ui/core';
import {
  makeStyles,
  alpha
} from '@material-ui/core/styles';
import {
  Edit as EditIcon,
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon
} from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { Alert } from '@material-ui/lab';
import userService from '../../services/UserService';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
    minWidth: 500,
    maxWidth: 600,
  },
  dialogTitle: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    color: 'white',
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  avatarContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
  },
  avatar: {
    width: 80,
    height: 80,
    backgroundColor: '#f26522',
    fontSize: '2rem',
    fontWeight: 'bold',
    boxShadow: '0 4px 20px rgba(242, 101, 34, 0.3)',
  },
  sectionTitle: {
    color: '#226D68',
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  textField: {
    marginBottom: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      '&:hover fieldset': {
        borderColor: '#226D68',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#f26522',
      },
    },
  },
  saveButton: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    color: 'white',
    borderRadius: 25,
    padding: theme.spacing(1, 3),
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #1a524d 0%, #226D68 100%)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 15px rgba(34, 109, 104, 0.4)',
    },
    transition: 'all 0.3s ease',
  },
  cancelButton: {
    color: '#666',
    borderRadius: 25,
    padding: theme.spacing(1, 3),
    fontWeight: 600,
    textTransform: 'none',
    border: '1px solid #ddd',
    '&:hover': {
      backgroundColor: alpha('#ff4757', 0.1),
      color: '#ff4757',
    },
  },
  stepIcon: {
    color: '#226D68',
    '&.MuiStepIcon-active': {
      color: '#f26522',
    },
    '&.MuiStepIcon-completed': {
      color: '#226D68',
    },
  },
  infoCard: {
    borderRadius: 12,
    border: `1px solid ${alpha('#226D68', 0.1)}`,
    boxShadow: '0 4px 20px rgba(34, 109, 104, 0.1)',
    marginBottom: theme.spacing(3),
  },
  infoItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
  },
  infoLabel: {
    fontWeight: 600,
    color: '#666',
  },
  infoValue: {
    fontWeight: 500,
    color: '#226D68',
  },
  successIcon: {
    color: '#4caf50',
    fontSize: 48,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
}));

const UserProfile = ({ open, onClose }) => {
  const classes = useStyles();
  const user = useSelector((state) => state.auth);
  console.log("user")
  console.log(user)
  console.log("user")
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [profileLoading, setProfileLoading] = useState(false);

  // États pour les informations utilisateur
  const [userInfo, setUserInfo] = useState({
    nom: '',
    prenom: '',
    speudo: '',
  });

  // États pour le changement de mot de passe
  const [passwordInfo, setPasswordInfo] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (open && user && user.id) {
        setProfileLoading(true);
        try {
          const response = await userService.getUserProfile(user.id);
          const userData = response.data;
          console.log("-------------------")
          console.log(userData)
          console.log("-------------------")
          
          setUserInfo({
            nom: userData.nom || '',
            prenom: userData.prenom || '',
            speudo: userData.speudo, // selon le nom de champ dans votre API
          });
        } catch (error) {
          console.error('Erreur lors du chargement du profil:', error);
          // Fallback aux données du store Redux si l'API échoue
          setUserInfo({
            nom: user.nom || '',
            prenom: user.prenom || '',
            speudo: user.speudo || '',
          });
          showSnackbar(
            error.response?.data?.message || 
            'Erreur lors du chargement du profil', 
            'error'
          );
        } finally {
          setProfileLoading(false);
        }
      }
    };

    fetchUserProfile();
  }, [open, user]);

  const handleUserInfoChange = (field) => (event) => {
    setUserInfo({
      ...userInfo,
      [field]: event.target.value,
    });
  };

  const handlePasswordChange = (field) => (event) => {
    setPasswordInfo({
      ...passwordInfo,
      [field]: event.target.value,
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleUpdateProfile = async () => {
    if (!userInfo.nom || !userInfo.prenom || !userInfo.speudo) {
      showSnackbar('Veuillez remplir tous les champs obligatoires', 'error');
      return;
    }

    setLoading(true);
    try {
      // Préparer les données selon ce que votre API attend
      const updateData = {
        nom: userInfo.nom,
        prenom: userInfo.prenom,
        speudo: userInfo.speudo,
        rol: user.rol, // Conserver le rôle actuel
        passwd: userInfo.passwd // Conserver le mot de passe actuel
      };

      console.log(updateData)

      const response = await userService.updateUser(user.id, updateData);
      showSnackbar(response.data?.message || 'Profil mis à jour avec succès');
      setActiveStep(1);
    } catch (error) {
      console.error('Erreur mise à jour profil:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Erreur lors de la mise à jour du profil';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordInfo.newPassword !== passwordInfo.confirmPassword) {
      showSnackbar('Les mots de passe ne correspondent pas', 'error');
      return;
    }

    if (passwordInfo.newPassword.length < 6) {
      showSnackbar('Le mot de passe doit contenir au moins 6 caractères', 'error');
      return;
    }

    if (!passwordInfo.currentPassword) {
      showSnackbar('Veuillez saisir votre mot de passe actuel', 'error');
      return;
    }

    setLoading(true);
    try {
      const response = await userService.changePassword(user.id, {
        currentPassword: passwordInfo.currentPassword,
        newPassword: passwordInfo.newPassword,
      });
      showSnackbar(response.data?.message || 'Mot de passe modifié avec succès');
      setActiveStep(2);
      setPasswordInfo({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      console.error('Erreur changement mot de passe:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Erreur lors du changement de mot de passe';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setUserInfo({
      nom: user.nom || '',
      prenom: user.prenom || '',
      speudo: user.speudo || '',
    });
    setPasswordInfo({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    onClose();
  };

  const getInitials = () => {
    return `${userInfo.prenom?.charAt(0) || ''}${userInfo.nom?.charAt(0) || ''}`.toUpperCase();
  };

  const steps = ['Informations Personnelles', 'Sécurité', 'Terminé'];

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" className={classes.sectionTitle}>
              <PersonIcon />
              Informations Personnelles
            </Typography>
            
            <Card className={classes.infoCard}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography className={classes.infoLabel}>Nom</Typography>
                    <Typography className={classes.infoValue}>{userInfo.nom}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography className={classes.infoLabel}>Prénom</Typography>
                    <Typography className={classes.infoValue}>{userInfo.prenom}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider style={{ margin: '16px 0' }} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography className={classes.infoLabel}>Nom d'utilisateur</Typography>
                    <Typography className={classes.infoValue}>{userInfo.speudo}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography className={classes.infoLabel}>Rôle</Typography>
                    <Typography className={classes.infoValue}>{user?.rol?.toUpperCase()}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Typography variant="h6" className={classes.sectionTitle} style={{ marginTop: 24 }}>
              <EditIcon />
              Modifier les Informations
            </Typography>

            <TextField
              fullWidth
              label="Nom *"
              value={userInfo.nom}
              onChange={handleUserInfoChange('nom')}
              className={classes.textField}
              variant="outlined"
              required
            />
            <TextField
              fullWidth
              label="Prénom *"
              value={userInfo.prenom}
              onChange={handleUserInfoChange('prenom')}
              className={classes.textField}
              variant="outlined"
              required
            />
            <TextField
              fullWidth
              label="Nom d'utilisateur *"
              value={userInfo.speudo}
              onChange={handleUserInfoChange('speudo')}
              className={classes.textField}
              variant="outlined"
              required
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" className={classes.sectionTitle}>
              <SecurityIcon />
              Changer le Mot de Passe
            </Typography>

            <TextField
              fullWidth
              label="Mot de passe actuel *"
              type={showPasswords.current ? 'text' : 'password'}
              value={passwordInfo.currentPassword}
              onChange={handlePasswordChange('currentPassword')}
              className={classes.textField}
              variant="outlined"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility('current')}
                      edge="end"
                    >
                      {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Nouveau mot de passe *"
              type={showPasswords.new ? 'text' : 'password'}
              value={passwordInfo.newPassword}
              onChange={handlePasswordChange('newPassword')}
              className={classes.textField}
              variant="outlined"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility('new')}
                      edge="end"
                    >
                      {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirmer le nouveau mot de passe *"
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwordInfo.confirmPassword}
              onChange={handlePasswordChange('confirmPassword')}
              className={classes.textField}
              variant="outlined"
              required
              error={passwordInfo.newPassword !== passwordInfo.confirmPassword}
              helperText={
                passwordInfo.newPassword !== passwordInfo.confirmPassword 
                  ? 'Les mots de passe ne correspondent pas' 
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility('confirm')}
                      edge="end"
                    >
                      {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );

      case 2:
        return (
          <Box textAlign="center" py={4}>
            <CheckCircleIcon className={classes.successIcon} />
            <Typography variant="h6" style={{ color: '#4caf50', marginTop: 16 }}>
              Mise à jour réussie !
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Vos informations ont été mises à jour avec succès.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  const getStepActions = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <Button onClick={handleClose} className={classes.cancelButton}>
              Annuler
            </Button>
            <Button 
              onClick={handleUpdateProfile} 
              className={classes.saveButton}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Continuer'}
            </Button>
          </>
        );
      case 1:
        return (
          <>
            <Button onClick={() => setActiveStep(0)} className={classes.cancelButton}>
              Retour
            </Button>
            <Button 
              onClick={handleChangePassword} 
              className={classes.saveButton}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Changer le mot de passe'}
            </Button>
          </>
        );
      case 2:
        return (
          <Button onClick={handleClose} className={classes.saveButton}>
            Terminer
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        classes={{ paper: classes.dialogPaper }}
        maxWidth="md"
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.avatarContainer}>
            <Avatar className={classes.avatar}>
              {getInitials()}
            </Avatar>
          </Box>
          <Typography variant="h5" component="div">
            Mon Profil
          </Typography>
          <Typography variant="body2">
            Gérez vos informations personnelles et votre sécurité
          </Typography>
        </DialogTitle>

        <DialogContent style={{ padding: 24 }}>
          <Stepper activeStep={activeStep} alternativeLabel style={{ marginBottom: 32 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel StepIconProps={{ classes: { root: classes.stepIcon } }}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent(activeStep)}
        </DialogContent>

        <DialogActions style={{ padding: 24, justifyContent: 'space-between' }}>
          {getStepActions(activeStep)}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserProfile;